import { useMemo, useEffect } from "react";
import {
  Form,
  Row,
  Col,
  Card,
  Skeleton,
  Alert,
  Input,
  Button,
  Typography,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFormMetadata } from "../../hooks/useFormMetadata";
import { useResponsive } from "../../hooks";
import { DynamicFormSection } from "./DynamicFormSection";
import { FormItemTypeProvider } from "./FormItemTypeContext";
import { transformDatesToAPIValues } from "../../utils/dateUtils";
import { buildInitialValuesFromDefaults } from "../../utils/defaultValueUtils";
import type { DynamicFormProps } from "./types";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Dynamic form component that renders form fields based on metadata from API.
 *
 * Features:
 * - Fetches field metadata from Aras via API
 * - Renders appropriate input types based on dataType
 * - Applies validation rules from metadata
 * - Supports field grouping via sections
 * - Falls back to hardcoded fields if API fails
 * - Responsive layout for mobile/tablet
 */
export const DynamicForm = <T,>({
  itemTypeName,
  mode,
  formProps,
  form: formProp,
  initialValues,
  notesPlacement = "sidebar",
  excludeFields = [],
  customSections,
  customRenderers,
  sidebarExtra,
  onFormStatusChange,
  saveButtonProps,
  expandAllSections = false,
}: DynamicFormProps<T>) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [fallbackForm] = Form.useForm();
  // Use form from prop (from Refine's useForm), otherwise use fallback
  const form = formProp ?? fallbackForm;

  // Determine if this is read-only mode (show mode)
  const isReadOnly = mode === "show";

  // Fetch form metadata (including Responsive Form layout from Aras if available)
  // For "show" mode, we fetch metadata as if it's "edit" mode (same field visibility rules)
  const {
    fields,
    sections,
    hasResponsiveForm,
    defaultSiteId,
    isLoading,
    isError,
    error,
  } = useFormMetadata({
    itemTypeName,
    mode: mode === "show" ? "edit" : mode,
    enabled: true,
  });

  // Set default site_id on create mode when site scoping is enabled
  useEffect(() => {
    if (mode === "create" && defaultSiteId && !isLoading) {
      const currentSiteId = form.getFieldValue("siteId");
      if (!currentSiteId) {
        form.setFieldsValue({ siteId: defaultSiteId });
      }
    }
  }, [mode, defaultSiteId, isLoading, form]);

  // Apply Aras property default_value to form fields on create mode.
  // Only fill fields the user hasn't touched yet so we never overwrite input.
  useEffect(() => {
    if (mode !== "create" || isLoading || fields.length === 0) return;

    const defaults = buildInitialValuesFromDefaults(fields);
    const toApply: Record<string, unknown> = {};

    Object.entries(defaults).forEach(([name, value]) => {
      if (form.getFieldValue(name) === undefined) {
        toApply[name] = value;
      }
    });

    if (Object.keys(toApply).length > 0) {
      form.setFieldsValue(toApply);
    }
  }, [mode, isLoading, fields, form]);

  // Filter out additional excluded fields
  const filteredFields = useMemo(() => {
    const excludeSet = new Set(excludeFields);
    return fields.filter((f) => !excludeSet.has(f.mappedName));
  }, [fields, excludeFields]);

  // Get notes field
  const notesField = filteredFields.find((f) => f.mappedName === "notes");

  // Fields without notes (for main form)
  // When using Responsive Form, notes is part of the layout so include it in mainFields
  const mainFields = hasResponsiveForm
    ? filteredFields
    : filteredFields.filter((f) => f.mappedName !== "notes");

  // Use custom sections or default from metadata
  const activeSections = customSections || sections;

  // Calculate visible groupboxes (sections with title or bordered=true) for auto-collapse logic
  const visibleGroupboxIndices = useMemo(() => {
    const indices: number[] = [];
    activeSections.forEach((section, index) => {
      // A section is a visible groupbox if it has a title or bordered=true
      const hasTitle = section.title || section.titleKey;
      const isBordered = section.bordered === true;
      if (hasTitle || isBordered) {
        indices.push(index);
      }
    });
    return indices;
  }, [activeSections]);

  // Form is considered "not configured" if the ItemType doesn't have
  // a Responsive Form configured in Aras. All dynamic ItemTypes must
  // have their form layout defined in Aras.
  const isFormNotConfigured = !hasResponsiveForm;

  // On mobile, always put sidebar content below main form
  const effectiveNotesPlacement = isMobile ? "bottom" : notesPlacement;

  // Determine if we should show sidebar column
  const showSidebar =
    !isMobile &&
    ((notesPlacement === "sidebar" && !hasResponsiveForm) || sidebarExtra);

  // Notify parent about form configuration status
  useEffect(() => {
    onFormStatusChange?.({
      isConfigured: !isFormNotConfigured,
      isLoading,
    });
  }, [isFormNotConfigured, isLoading, onFormStatusChange]);

  // Loading state
  if (isLoading) {
    return (
      <Row gutter={isMobile ? 0 : 16}>
        <Col xs={24} lg={effectiveNotesPlacement === "sidebar" ? 16 : 24}>
          <Card size={isMobile ? "small" : "default"}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </Card>
        </Col>
        {!isMobile && effectiveNotesPlacement === "sidebar" && (
          <Col xs={24} lg={8}>
            <Card size={isMobile ? "small" : "default"}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          </Col>
        )}
      </Row>
    );
  }

  // Error state - show warning but continue with fallback fields
  const errorAlert = isError && (
    <Alert
      message={t("dynamicForm.metadataError")}
      description={error?.message || t("dynamicForm.usingFallback")}
      type="warning"
      showIcon
      style={{ marginBottom: isMobile ? 12 : 16 }}
    />
  );

  // Show notice when form is not configured or has no fields
  const noFormConfiguredNotice = isFormNotConfigured && (
    <Alert
      message={t("dynamicForm.noFormConfigured", {
        defaultValue: "Form chưa được cấu hình",
      })}
      description={t("dynamicForm.noFormConfiguredDesc", {
        itemType: itemTypeName,
        defaultValue: `Vui lòng liên hệ quản trị viên để thiết lập form.`,
      })}
      type="info"
      showIcon
      style={{ marginBottom: isMobile ? 12 : 16 }}
    />
  );

  // Render notes section
  // Don't show notes if form is not configured
  const renderNotes = () => {
    if (
      !notesField ||
      effectiveNotesPlacement === "none" ||
      isFormNotConfigured
    )
      return null;

    // In read-only mode, display notes as text
    if (isReadOnly) {
      const notesValue = form.getFieldValue("notes");
      return (
        <Card title={t("forms.notes")} size={isMobile ? "small" : "default"}>
          {notesValue ? (
            <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {notesValue}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Card>
      );
    }

    return (
      <Card title={t("forms.notes")} size={isMobile ? "small" : "default"}>
        <Form.Item name="notes">
          <TextArea
            rows={isMobile ? 6 : 10}
            placeholder={t("forms.addNotes")}
          />
        </Form.Item>
      </Card>
    );
  };

  // NOTE: Unsectioned fields are no longer rendered.
  // All dynamic ItemTypes must use Responsive Form from Aras.
  // If no Responsive Form is configured, the "Form chưa được cấu hình" notice is shown.
  const renderUnsectionedFields = () => null;

  const handleFinish = (values: unknown) => {
    if (formProps?.onFinish) {
      // Use the full form store (including values held in collapsed/unmounted
      // Form.Item sections) instead of only what AntD's onFinish surfaces —
      // otherwise duplicate-prefilled sections that the user didn't expand
      // get silently dropped from the create payload.
      // Shallow merge is intentional: AntD `values` already returns nested
      // NamePath subtrees in their final shape, so spreading them over the
      // store correctly overrides whole subtrees rather than merging them.
      const allValues = {
        ...(form.getFieldsValue(true) as Record<string, unknown>),
        ...(values as Record<string, unknown>),
      };
      const transformedValues = transformDatesToAPIValues(allValues);
      return formProps.onFinish(transformedValues);
    }
  };

  return (
    <FormItemTypeProvider value={itemTypeName}>
      <Form
        {...formProps}
        form={form}
        layout="vertical"
        initialValues={initialValues as Record<string, unknown>}
        onFinish={handleFinish}
      >
        {errorAlert}

        <Row gutter={isMobile ? 0 : 16}>
          {/* Main form content */}
          {/* When using Responsive Form, notes is inline so use full width unless sidebarExtra */}
          <Col xs={24} sm={24} md={24} lg={showSidebar ? 16 : 24}>
            {/* Show notice in center when form not configured */}
            {isFormNotConfigured && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                {noFormConfiguredNotice}
              </div>
            )}

            {/* Render sections - skip if form not configured */}
            {!isFormNotConfigured &&
              activeSections.map((section, index) => {
                // Determine if this section should be collapsed by default
                // Auto-collapse from 2nd groupbox onwards when there are multiple groupboxes
                // BUT: Never collapse sections that contain required fields (similar to Aras behavior)
                const groupboxPosition = visibleGroupboxIndices.indexOf(index);
                const isVisibleGroupbox = groupboxPosition !== -1;

                // Check if section contains any required fields
                const sectionFieldNames = new Set(section.fields);
                const hasRequiredFields = mainFields.some(
                  (f) => sectionFieldNames.has(f.mappedName) && f.isRequired
                );

                // Only collapse if:
                // 1. It's a visible groupbox (has title/border)
                // 2. It's not the first groupbox
                // 3. There are multiple groupboxes
                // 4. It does NOT contain required fields
                const shouldCollapse =
                  !expandAllSections &&
                  isVisibleGroupbox &&
                  groupboxPosition > 0 &&
                  visibleGroupboxIndices.length > 1 &&
                  !hasRequiredFields;

                return (
                  <DynamicFormSection
                    key={section.key}
                    config={section}
                    fields={mainFields}
                    form={form}
                    readOnly={isReadOnly}
                    customRenderers={customRenderers}
                    defaultCollapsed={shouldCollapse}
                  />
                );
              })}

            {/* Render unsectioned fields */}
            {renderUnsectionedFields()}

            {/* Notes at bottom (for non-responsive forms or on mobile) */}
            {effectiveNotesPlacement === "bottom" &&
              !hasResponsiveForm &&
              renderNotes()}
          </Col>

          {/* Sidebar content - only on desktop */}
          {/* When using Responsive Form, notes is part of the layout so don't show in sidebar */}
          {showSidebar && (
            <Col xs={24} sm={24} md={24} lg={8}>
              {sidebarExtra && (
                <div style={{ marginBottom: 16 }}>{sidebarExtra}</div>
              )}
              {notesPlacement === "sidebar" &&
                !hasResponsiveForm &&
                renderNotes()}
            </Col>
          )}
        </Row>

        {/* On mobile, show sidebar content below the form */}
        {isMobile &&
          (sidebarExtra ||
            (notesPlacement === "sidebar" &&
              !hasResponsiveForm &&
              notesField)) && (
            <div style={{ marginTop: 16 }}>
              {sidebarExtra}
              {notesPlacement === "sidebar" &&
                !hasResponsiveForm &&
                renderNotes()}
            </div>
          )}

        {/* Save button - only show when form is configured, saveButtonProps provided, and not read-only */}
        {saveButtonProps && !isFormNotConfigured && !isReadOnly && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 24,
              paddingTop: 16,
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              {...saveButtonProps}
            >
              {t("buttons.save", "Lưu")}
            </Button>
          </div>
        )}
      </Form>
    </FormItemTypeProvider>
  );
};

export { DynamicFormField } from "./DynamicFormField";
export { DynamicFormFieldDisplay } from "./DynamicFormFieldDisplay";
export { DynamicFormSection } from "./DynamicFormSection";
export { FileUploadField } from "./FileUploadField";
export {
  RelatedItemCreateModal,
  getResourceFromItemType,
} from "./RelatedItemCreateModal";
export type {
  DynamicFormProps,
  DynamicFormFieldProps,
  DynamicFormSectionProps,
} from "./types";
